const FALLBACK_BACKGROUND = '#FAF6F2';
const FALLBACK_FOREGROUND = '#1A140F';
export const BACKGROUND_PATTERN_PLACEHOLDER = '#1A140F12';

export const DEFAULT_BACKGROUND_PATTERN = 'none';
export const DEFAULT_BACKGROUND_PATTERN_OPACITY = 100;

export const BACKGROUND_PATTERN_OPTIONS = [
  { id: 'none', label: 'No pattern' },
  { id: 'dots', label: 'Dots' },
  { id: 'horizontal', label: 'Horizontal lines' },
  { id: 'grid', label: 'Grid' },
  { id: 'crosshatch', label: 'Crosshatch' },
  { id: 'plus', label: 'Plus signs' },
  { id: 'zigzag', label: 'Zigzag' },
  { id: 'waves', label: 'Waves' },
  { id: 'diamonds', label: 'Diamonds' },
];

const PATTERN_DEFINITIONS = {
  dots: {
    size: '18px 18px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
        <circle cx="9" cy="9" r="2" fill="${color}" />
      </svg>
    `,
  },
  diagonal: {
    size: '28px 28px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
        <path d="M-7 28L28 -7M7 35L35 7" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      </svg>
    `,
  },
  horizontal: {
    size: '28px 28px',
    svg: (color) => `
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_1418_4027)">
          <path d="M28 0H0V28" stroke="${color}"/>
        </g>
        <defs>
          <clipPath id="clip0_1418_4027">
            <rect width="28" height="28" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    `,
  },
  grid: {
    size: '24px 24px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M0 .5H24M0 12.5H24M0 23.5H24M.5 0V24M12.5 0V24M23.5 0V24" stroke="${color}" stroke-width="1" />
      </svg>
    `,
  },
  crosshatch: {
    size: '24px 24px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M0 24L24 0M0 0L24 24" stroke="${color}" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    `,
  },
  plus: {
    size: '28px 28px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
        <path d="M14 6V22M6 14H22" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      </svg>
    `,
  },
  zigzag: {
    size: '32px 16px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="16" viewBox="0 0 32 16">
        <path d="M0 12L8 4L16 12L24 4L32 12" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `,
  },
  waves: {
    size: '32px 16px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="16" viewBox="0 0 32 16">
        <path d="M0 8C4 2 12 2 16 8C20 14 28 14 32 8" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" />
      </svg>
    `,
  },
  diamonds: {
    size: '28px 28px',
    svg: (color) => `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
        <path d="M14 4L24 14L14 24L4 14Z" fill="none" stroke="${color}" stroke-width="2" />
      </svg>
    `,
  },
};

const clampOpacity = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return DEFAULT_BACKGROUND_PATTERN_OPACITY;
  }

  return Math.max(0, Math.min(100, Math.round(parsedValue)));
};

const hexToRgba = (hex, alpha) => {
  const normalizedHex = (hex || '').replace('#', '').trim();

  if (normalizedHex.length !== 3 && normalizedHex.length !== 6) {
    return `rgba(26, 20, 15, ${alpha})`;
  }

  const fullHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalizedHex;

  const red = Number.parseInt(fullHex.slice(0, 2), 16);
  const green = Number.parseInt(fullHex.slice(2, 4), 16);
  const blue = Number.parseInt(fullHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const buildPatternDataUri = (patternId, color) => {
  const definition = PATTERN_DEFINITIONS[patternId];

  if (!definition) {
    return 'none';
  }

  const svg = definition.svg(color);

  return `url("data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}")`;
};

const getPatternBackgroundSize = (patternId) => {
  const definition = PATTERN_DEFINITIONS[patternId];
  const fallbackSize = definition?.size || '28px 28px';

  if (!patternId) {
    return fallbackSize;
  }

  return `var(--background-pattern-size-${patternId}, ${fallbackSize})`;
};

export const getBackgroundPatternStyle = ({
  pattern = DEFAULT_BACKGROUND_PATTERN,
  opacity = DEFAULT_BACKGROUND_PATTERN_OPACITY,
  backgroundColor = FALLBACK_BACKGROUND,
  foregroundColor = FALLBACK_FOREGROUND,
} = {}) => {
  const normalizedOpacity = clampOpacity(opacity);
  const patternColor = hexToRgba(foregroundColor, normalizedOpacity / 100);
  const backgroundImage = buildPatternDataUri(pattern, patternColor);

  return {
    backgroundColor: backgroundColor || FALLBACK_BACKGROUND,
    backgroundImage,
    backgroundPosition: 'center',
    backgroundRepeat: 'repeat',
    backgroundSize: getPatternBackgroundSize(pattern),
  };
};
